import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileSpreadsheet, Download, Upload, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const TEMPLATE = 'empresa;dono;whatsapp;email;cpf_cnpj;plano;desconto\nPadaria Exemplo;Maria Silva;31999998888;;;free;\nPizzaria Modelo;Joao Souza;31988887777;joao@email.com;12345678000190;enterprise;10\n';
const HEADER_MAP = { empresa: 'company_name', dono: 'owner_name', whatsapp: 'whatsapp', email: 'owner_email', cpf_cnpj: 'cpf_cnpj', cnpj: 'cpf_cnpj', plano: 'plan_id', desconto: 'discount_pct' };

const parseCsv = (text) => {
  const clean = text.replace(/^\uFEFF/, '').trim();
  const lines = clean.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return { error: 'Planilha vazia (só cabeçalho ou nada)' };
  const delim = lines[0].includes(';') ? ';' : ',';
  const headers = lines[0].split(delim).map(h => HEADER_MAP[h.trim().toLowerCase()] || null);
  if (!headers.includes('company_name')) return { error: 'Cabeçalho inválido — baixe o modelo e mantenha a 1ª linha' };
  const rows = lines.slice(1).map(line => {
    const cols = line.split(delim);
    const row = {};
    headers.forEach((h, i) => { if (h) row[h] = (cols[i] || '').trim(); });
    return row;
  });
  return { rows };
};

export const BatchImplantCard = ({ batchUrl, statusUrl, headers = {} }) => {
  const fileRef = useRef(null);
  const [rows, setRows] = useState(null);
  const [fileName, setFileName] = useState('');
  const [errors, setErrors] = useState([]);
  const [validating, setValidating] = useState(false);
  const [batch, setBatch] = useState(null);

  const cfg = { headers };

  const downloadTemplate = () => {
    const blob = new Blob(['\uFEFF' + TEMPLATE], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'modelo-clientes-falocomodono.csv';
    a.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name); setBatch(null); setErrors([]);
    const text = await file.text();
    const parsed = parseCsv(text);
    if (parsed.error) { toast.error(parsed.error); setRows(null); return; }
    setRows(parsed.rows);
    setValidating(true);
    try {
      const r = await axios.post(batchUrl, { rows: parsed.rows, validate_only: true }, cfg);
      setErrors(r.data.errors || []);
      if (r.data.valid) toast.success(`${parsed.rows.length} linhas válidas — pronto para cadastrar`);
      else toast.error(`${r.data.errors.length} erro(s) na planilha`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao validar'); }
    finally { setValidating(false); }
  };

  const startBatch = async () => {
    try {
      const r = await axios.post(batchUrl, { rows }, cfg);
      if (!r.data.valid) { setErrors(r.data.errors || []); toast.error('Planilha tem erros'); return; }
      setBatch({ id: r.data.batch_id, total: r.data.total, done: 0, results: [], status: 'processing' });
      const poll = setInterval(async () => {
        try {
          const p = await axios.get(statusUrl(r.data.batch_id), cfg);
          setBatch(p.data);
          if (p.data.status === 'finished') { clearInterval(poll); toast.success('Lote concluído! Kits enviados no WhatsApp 🎉'); }
        } catch { clearInterval(poll); }
      }, 3000);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao iniciar lote'); }
  };

  const reset = () => { setRows(null); setErrors([]); setBatch(null); setFileName(''); if (fileRef.current) fileRef.current.value = ''; };

  return (
    <Card data-testid="batch-implant-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><FileSpreadsheet className="w-5 h-5 text-[#C5A059]" /> Cadastro em Lote (Planilha)</CardTitle>
        <p className="text-sm text-slate-500">Para redes e associações: suba um CSV e o sistema cadastra todas as empresas, enviando o kit no WhatsApp de cada dono (com intervalo anti-spam). Plano vazio/free = trial grátis · plano pago = boleto em 3 dias.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {!batch && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template-btn">
              <Download className="w-4 h-4 mr-1" /> Baixar modelo CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="upload-csv-btn">
              <Upload className="w-4 h-4 mr-1" /> {fileName || 'Enviar planilha preenchida'}
            </Button>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="csv-file-input" />
          </div>
        )}

        {validating && <p className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Validando planilha...</p>}

        {errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1 max-h-48 overflow-y-auto" data-testid="batch-errors">
            <p className="text-sm font-bold text-red-700">Corrija a planilha e envie de novo:</p>
            {errors.map((e, i) => <p key={i} className="text-xs text-red-600">Linha {e.row}: {e.error}</p>)}
          </div>
        )}

        {rows && errors.length === 0 && !validating && !batch && (
          <div className="space-y-3">
            <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-lg">
              <table className="w-full text-xs" data-testid="batch-preview-table">
                <thead className="bg-slate-50 sticky top-0"><tr>
                  {['#', 'Empresa', 'Dono', 'WhatsApp', 'Plano', 'Desc.'].map(h => <th key={h} className="text-left px-2 py-1.5 font-semibold text-slate-500">{h}</th>)}
                </tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-2 py-1 text-slate-400">{i + 1}</td>
                      <td className="px-2 py-1 font-medium">{r.company_name}</td>
                      <td className="px-2 py-1">{r.owner_name}</td>
                      <td className="px-2 py-1">{r.whatsapp}</td>
                      <td className="px-2 py-1">{r.plan_id || 'free'}</td>
                      <td className="px-2 py-1">{r.discount_pct ? `${r.discount_pct}%` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button onClick={startBatch} className="w-full bg-[#0A2647] hover:bg-[#0e3260] text-white font-bold" data-testid="start-batch-btn">
              Cadastrar {rows.length} empresa{rows.length > 1 ? 's' : ''} agora
            </Button>
          </div>
        )}

        {batch && (
          <div className="space-y-3" data-testid="batch-progress">
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold text-[#0A2647]">{batch.status === 'finished' ? '✅ Lote concluído' : '⏳ Cadastrando...'}</span>
              <span className="text-slate-500">{batch.done}/{batch.total}</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2.5">
              <div className="bg-[#C5A059] h-2.5 rounded-full transition-all" style={{ width: `${(batch.done / batch.total) * 100}%` }} />
            </div>
            {(batch.results || []).length > 0 && (
              <div className="max-h-48 overflow-y-auto space-y-1">
                {batch.results.map((r, i) => (
                  <p key={i} className="text-xs flex items-center gap-1.5">
                    {r.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />}
                    <span className="font-medium">{r.company_name}</span>
                    {r.ok ? <span className="text-slate-400">/{r.slug}{r.final_price ? ` · R$ ${r.final_price.toFixed(2).replace('.', ',')}/mês` : ''}</span> : <span className="text-red-500">{r.error}</span>}
                  </p>
                ))}
              </div>
            )}
            {batch.status === 'finished' && <Button variant="outline" size="sm" onClick={reset} data-testid="batch-reset-btn">Novo lote</Button>}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BatchImplantCard;
